/**
 * "What the icons mean": the one legend component the Help page, training module 4, the
 * cheat sheet and the queue help pop-up share (round 3, 12 Oct 2026). The lines are the
 * ten LEGEND sentences of `lib/line-icons.ts`, drawn with the icons each line names, in the
 * tones the draft table uses. `compact` keeps the first four lines (the four status
 * words) for the small pop-up.
 */

import { Icon, type IconComponent, type IconTone } from "@norican/icons";
import { Typography } from "@norican/react";

import {
  BASIS_ICON,
  LEAD_ICON,
  LEGEND,
  PRICE_ICON,
  QTY_DEFAULT_ICON,
  QTY_ISSUE_ICON,
  QUEUE_ICON,
  SOURCE_ICON,
  STATUS_ICON,
  STATUS_TONE,
} from "@/lib/line-icons";

import { Figure } from "./figure";

export const STATUS_LEGEND_TITLE = "What the icons mean";

/** One line: the icons as the table draws them, then the sentence. */
export interface LegendItem {
  icons: readonly { icon: IconComponent; tone?: IconTone }[];
  text: string;
}

function meaning(index: number): string {
  return LEGEND[index]?.meaning ?? "";
}

/** The ten lines, in LEGEND order, with the tones of the table. */
export const STATUS_LEGEND_ITEMS: readonly LegendItem[] = [
  { icons: [{ icon: STATUS_ICON.matched, tone: STATUS_TONE.matched }], text: meaning(0) },
  { icons: [{ icon: STATUS_ICON.confirm, tone: STATUS_TONE.confirm }], text: meaning(1) },
  { icons: [{ icon: STATUS_ICON.pick, tone: STATUS_TONE.pick }], text: meaning(2) },
  { icons: [{ icon: STATUS_ICON["no item"] }], text: meaning(3) },
  {
    icons: [
      { icon: SOURCE_ICON.mail },
      { icon: SOURCE_ICON.attachment },
      { icon: SOURCE_ICON.photo },
      { icon: SOURCE_ICON.scan },
    ],
    text: meaning(4),
  },
  {
    icons: [
      { icon: BASIS_ICON.exact_number },
      { icon: BASIS_ICON.alias },
      { icon: BASIS_ICON.earlier_mail },
      { icon: BASIS_ICON.similar },
      { icon: BASIS_ICON.person },
    ],
    text: meaning(5),
  },
  {
    icons: [
      { icon: PRICE_ICON.in_band, tone: "success" },
      { icon: PRICE_ICON.below_margin, tone: "warning" },
      { icon: PRICE_ICON.none },
    ],
    text: meaning(6),
  },
  {
    icons: [
      { icon: LEAD_ICON.known },
      { icon: LEAD_ICON.historic },
      { icon: LEAD_ICON.none },
      { icon: LEAD_ICON.not_looked_up },
    ],
    text: meaning(7),
  },
  {
    icons: [{ icon: QTY_ISSUE_ICON, tone: "warning" }, { icon: QTY_DEFAULT_ICON }],
    text: meaning(8),
  },
  {
    icons: [
      { icon: QUEUE_ICON.Confirm },
      { icon: QUEUE_ICON.Pick },
      { icon: QUEUE_ICON.Check },
      { icon: QUEUE_ICON["Copy reply"] },
    ],
    text: meaning(9),
  },
];

/** How many lines `compact` keeps: the four status words. */
export const COMPACT_LINES = 4;

/**
 * The icons of the six cheat-sheet lines, in cheat-sheet order: matched, confirm, pick,
 * no item, the price band (check badge, warning triangle, dashed circle), the lead time
 * (clock, hourglass). The cheat sheet prints, so it draws them without a tone.
 */
export const CHEAT_LEGEND_ICONS: readonly (readonly IconComponent[])[] = [
  [STATUS_ICON.matched],
  [STATUS_ICON.confirm],
  [STATUS_ICON.pick],
  [STATUS_ICON["no item"]],
  [PRICE_ICON.in_band, PRICE_ICON.below_margin, PRICE_ICON.none],
  [LEAD_ICON.known, LEAD_ICON.historic, LEAD_ICON.none, LEAD_ICON.not_looked_up],
];

export interface StatusLegendProps {
  /** The lines to draw. Defaults to the ten lines of LEGEND. */
  items?: readonly LegendItem[];
  /** A label over the list. */
  heading?: string;
  /** The first four lines only (the four status words). */
  compact?: boolean;
  /** One sentence under the list, muted. */
  caption?: string;
}

/**
 * The bare legend list (no frame): the help's legend block and the queue pop-up embed
 * it. The icons are decorative because every sentence names its icons.
 */
export function StatusLegend({
  items = STATUS_LEGEND_ITEMS,
  heading,
  compact = false,
  caption,
}: StatusLegendProps) {
  const shown = compact ? items.slice(0, COMPACT_LINES) : items;
  return (
    <div className="flex min-w-0 flex-col gap-2" data-status-legend={compact ? "compact" : "full"}>
      {heading && <Typography variant="label">{heading}</Typography>}
      <ul className="flex flex-col gap-1.5">
        {shown.map((item) => (
          <li key={item.text} className="flex items-start gap-3">
            <span className="text-fg-muted mt-0.5 flex shrink-0 items-center gap-1">
              {item.icons.map((entry, index) => (
                <Icon key={index} icon={entry.icon} size="sm" tone={entry.tone} />
              ))}
            </span>
            <span className="text-fg min-w-0 text-sm break-words">{item.text}</span>
          </li>
        ))}
      </ul>
      {caption && (
        <Typography variant="caption" tone="muted" className="break-words">
          {caption}
        </Typography>
      )}
    </div>
  );
}

/** The legend in the figure frame, for the training and help figure blocks. */
export function StatusLegendFigure({ caption }: { caption?: string }) {
  return (
    <Figure name="status-legend" title={STATUS_LEGEND_TITLE} caption={caption}>
      <StatusLegend />
    </Figure>
  );
}
